import { create } from "zustand";
import type { WidgetRequest, WidgetState, WidgetUpdateRequest, WidgetPreviewRequest, WidgetAggregationPreviewRequest } from "../types";
import { widgetApi } from "../services/widget-api";

export type WidgetPlanLimitErrorInfo = {
	feature: string;
	limit?: number;
	current_count?: number;
	message: string;
};

export class WidgetPlanLimitError extends Error {
	feature: string;
	limit?: number;
	current_count?: number;

	constructor(info: WidgetPlanLimitErrorInfo) {
		super(info.message);
		this.name = "WidgetPlanLimitError";
		this.feature = info.feature;
		this.limit = info.limit;
		this.current_count = info.current_count;
	}
}

export const useWidgetStore = create<WidgetState>((set) => ({
	widgets: null,
	templates: null,
	templateDetail: null,
	index: async (workspaceId: string) => {
		const response = await widgetApi.index(workspaceId);
		set({ widgets: response.data });
		return response;
	},
	show: async (id: string, workspaceId: string) => {
		const response = await widgetApi.show(id, workspaceId);
		return response;
	},
	create: async (workspaceId: string, payload: WidgetRequest) => {
		const response = await widgetApi.create(workspaceId, payload);
		set((state) => ({ widgets: state.widgets && response.data ? [...state.widgets, response.data] : response.data ? [response.data] : state.widgets ?? [] }));
		return response;
	},
	update: async (id: string, workspaceId: string, payload: WidgetUpdateRequest) => {
		const response = await widgetApi.update(id, workspaceId, payload);
		return response;
	},
	delete: async (id: string, workspaceId: string) => {
		const response = await widgetApi.delete(id, workspaceId);
		if (response.success) {
			set((state) => ({ widgets: state.widgets ? state.widgets.filter((widget) => widget.id !== id) : state.widgets }));
		}
		return response;
	},
	preview: async (workspaceId: string, payload: WidgetPreviewRequest, cursor?: string | null) => {
		const response = await widgetApi.preview(workspaceId, payload, cursor);
		return response;
	},
	listTemplates: async () => {
		const response = await widgetApi.listTemplates();
		if (response.success && response.data) {
			set({ templates: response.data });
		}
		return response;
	},
	showTemplate: async (template: string, version: number) => {
		const response = await widgetApi.showTemplate(template, version);
		if (response.success && response.data) {
			set({ templateDetail: response.data });
		}
		return response;
	},
	indexPinnedReviewIds: async (workspaceId: string, widgetId: string) => {
		const response = await widgetApi.indexPinnedReviewIds(workspaceId, widgetId);
		return response;
	},
	indexCuratedReviewIds: async (workspaceId: string, widgetId: string) => {
		const response = await widgetApi.indexCuratedReviewIds(workspaceId, widgetId);
		return response;
	},
syncPinnedReviews: async (workspaceId: string, widgetId: string, reviewIds: string[]) => {
		const response = await widgetApi.syncPinnedReviews(workspaceId, widgetId, reviewIds);
		if (response.status === 402 && response.error?.error === "plan_upgrade_required") {
			throw new WidgetPlanLimitError({
				feature: response.error.feature,
				limit: response.error.limit,
				current_count: response.error.current_count,
				message: response.message || "Plan limit exceeded",
			});
		}
		return response;
	},

	syncCuratedReviews: async (workspaceId: string, widgetId: string, reviewIds: string[]) => {
		const response = await widgetApi.syncCuratedReviews(workspaceId, widgetId, reviewIds);
		if (response.status === 402 && response.error?.error === "plan_upgrade_required") {
			throw new WidgetPlanLimitError({
				feature: response.error.feature,
				limit: response.error.limit,
				current_count: response.error.current_count,
				message: response.message || "Plan limit exceeded",
			});
		}
		return response;
	},
	aggregationPreview: async (workspaceId: string, payload: WidgetAggregationPreviewRequest) => {
		const response = await widgetApi.aggregationPreview(workspaceId, payload);
		return response;
	},
}));
